import React from 'react';
import { editorialArtwork } from './editorialArtwork';
import { Link, useParams } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import SEO from '../components/SEO';
import { ArticleStructuredData } from '../components/StructuredData';
import { formatPostDate, getPostBySlug, getRelatedPosts } from './posts';
import { getTopicForTags } from '../resources/topics';

const SITE_URL = 'https://www.greyfielddata.com';

function CategoryPill({ category, dark = false }: { category: string; dark?: boolean }) {
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${dark ? 'bg-white/10 text-blue-100 ring-1 ring-inset ring-white/15' : 'bg-blue-50 text-[#1a4e94]'}`}>
      {category}
    </span>
  );
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <BlogLayout>
        <SEO title="Post not found" description="This article does not exist." url={`${SITE_URL}/blog`} noIndex />
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Post not found</h1>
        <Link to="/blog" className="text-blue-600 hover:text-blue-800">
          Back to the blog
        </Link>
      </BlogLayout>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const socialImage = post.cover ? `${SITE_URL}${post.cover}` : undefined;
  const related = getRelatedPosts(post);
  const topic = getTopicForTags(post.tags);
  const isSigma = post.tags.includes('sigma');
  const isMigration = isSigma && post.tags.includes('migration');
  const source = isSigma && post.tags.includes('powerbi') && !post.tags.includes('tableau') ? 'Power BI'
    : isSigma && post.tags.includes('tableau') && !post.tags.includes('powerbi') ? 'Tableau' : null;
  const nextStep = post.tags.includes('ecommerce')
    ? { title: 'Need your ecommerce reports to agree?', body: 'Bring the reports that disagree and the decision they need to support. We can review the sources, definitions, and gaps together.', href: '/ecommerce', label: 'Explore ecommerce reporting help', booking: 'Discuss your reporting problem' }
    : source
      ? { title: `Planning a ${source} to Sigma migration?`, body: 'Start with one representative report. Review its calculations, data dependencies, and acceptance checks before scoping the rebuild.', href: source === 'Power BI' ? '/services/power-bi-to-sigma' : '/services/tableau-to-sigma', label: `Explore ${source} migration services`, booking: 'Discuss your migration' }
      : isSigma
        ? { title: isMigration ? 'Planning your Sigma migration?' : 'Need help building this in Sigma?', body: 'Bring a workbook or reporting question. We can review the data, calculations, and workflow with you.', href: '/services/sigma-consulting', label: 'Explore Sigma consulting', booking: isMigration ? 'Discuss your migration' : 'Discuss your Sigma workbook' }
        : post.tags.includes('snowflake')
          ? { title: 'Need help with your Snowflake setup?', body: 'Bring the query, connection, or reporting problem you need to solve. We can review the setup and next steps.', href: '/services/snowflake-consulting', label: 'Explore Snowflake consulting', booking: 'Discuss your Snowflake setup' }
          : { title: 'Need help with your reporting?', body: 'Bring a reporting question and the decision it needs to support.', href: '/call-expectations', label: 'See what a first call is like', booking: 'Discuss your reporting problem' };

  return (
    <BlogLayout>
      <SEO
        title={post.title}
        description={post.description}
        type="article"
        url={url}
        image={socialImage}
        publishedTime={post.date}
        modifiedTime={post.updated}
        keywords={post.keywords}
      />
      <ArticleStructuredData
        headline={post.title}
        description={post.description}
        url={url}
        image={socialImage}
        datePublished={post.date}
        dateModified={post.updated}
      />
      <article>
        <header className="mb-8 border-b border-slate-200 pb-8">
          <CategoryPill category={post.category} />
          <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">
            {post.title}
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-600">{post.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
            <div className="flex items-center gap-3">
              <img src="/images/linus-tse-linkedin-photo.jpeg" alt="" className="h-10 w-10 rounded-full object-cover" />
              <div className="text-sm">
                <Link to="/linus-tse-introduction" className="font-semibold text-slate-900 hover:text-blue-700">Linus Tse</Link>
                <p className="text-slate-500">Greyfield Data</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <time dateTime={post.date}>{formatPostDate(post.date)}</time>
              {post.updated && post.updated >= post.date && (
                <span>Updated <time dateTime={post.updated}>{formatPostDate(post.updated)}</time></span>
              )}
              <span aria-hidden="true">·</span>
              <span>{post.readingMinutes} min read</span>
            </div>
          </div>
          {editorialArtwork.has(post.slug) && (
            <img
              src={`/images/blog/${post.slug}/editorial.webp`}
              alt=""
              width="1440"
              height="480"
              decoding="async"
              className="mt-7 block aspect-[3/1] w-full rounded-xl bg-slate-950 object-cover"
            />
          )}
        </header>

        {post.headings.length >= 3 && (
          <details key={post.slug} className="mb-8 border-b border-slate-200 pb-5">
            <summary className="cursor-pointer text-sm font-semibold text-slate-700 hover:text-blue-700">
              In this article <span className="ml-2 font-normal text-slate-500">{post.headings.length} sections</span>
            </summary>
            <nav aria-label="Table of contents" className="pt-4">
              <ol className="grid list-decimal gap-x-8 gap-y-2 pl-5 sm:grid-cols-2 marker:text-slate-400">
                {post.headings.map((heading) => (
                  <li key={heading.id} className="pl-1">
                    <a href={`#${heading.id}`} className="block py-1 text-sm leading-snug text-slate-600 hover:text-blue-700">
                      {heading.text.replace(/^\d+\.\s+/, '')}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </details>
        )}

        <div className="md-article" dangerouslySetInnerHTML={{ __html: post.html }} />

        {topic && (
          <p className="mt-12 rounded-xl border border-slate-200 px-5 py-4 text-sm text-slate-600">
            Part of the <Link to={`/resources/${topic.slug}`} className="font-semibold text-[#1a4e94] underline underline-offset-4">{topic.title}</Link>, a reading path through related articles.
          </p>
        )}

        <aside aria-label="Get help with this topic" className="mt-14 rounded-2xl border border-blue-100 bg-slate-50 p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-slate-950">{nextStep.title}</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-slate-600">{nextStep.body}</p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <a href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true" target="_blank" rel="noopener noreferrer" className="inline-flex rounded-xl bg-[#1a4e94] px-5 py-3 font-semibold text-white hover:bg-blue-800">{nextStep.booking}</a>
            <Link to={nextStep.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{nextStep.label}</Link>
          </div>
        </aside>

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Related</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/blog/${item.slug}`}
                  className="group overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg"
                >
                  {item.cover && (
                    <img src={item.cover} alt="" width="1200" height="630" loading="lazy" decoding="async" className="aspect-[1200/630] w-full bg-[#071525] object-cover" />
                  )}
                  <div className="p-6">
                  <CategoryPill category={item.category} />
                  <h3 className="mt-4 text-xl font-semibold leading-snug text-gray-900 transition-colors group-hover:text-[#1a4e94]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="mt-12">
          <Link to="/blog" className="text-blue-600 hover:text-blue-800">
            ← All articles
          </Link>
        </p>
      </article>
    </BlogLayout>
  );
}
